import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Label } from '@/shared/components/ui/label';
import { Button } from '@/shared/components/ui/button';
import { Separator } from '@/shared/components/ui/separator';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/core/lib/utils';
import {
  ExistingFieldsList,
  FieldTypeList,
  FieldCreateForm,
  resolveFieldType,
  FIELD_REGISTRY,
  FIELD_TYPE,
  useProjectFields,
} from '@/features/custom-fields/public';
import type { FieldType, ProjectField } from '@/features/custom-fields/public';
import type { TaskColumnId } from '../../types';
import { BUILT_IN_COLUMN_DEFS } from './columnRegistry';

type PanelState =
  | { view: 'list' }
  | { view: 'create'; fieldType: FieldType }
  | { view: 'edit'; field: ProjectField };

interface ColumnSelectorPanelProps {
  visibleColumns: TaskColumnId[];
  onColumnsChange: (columns: TaskColumnId[]) => void;
  projectId?: string;
}

export function ColumnSelectorPanel({
  visibleColumns,
  onColumnsChange,
  projectId,
}: ColumnSelectorPanelProps) {
  const { t } = useTranslation('tasks');
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<PanelState>({ view: 'list' });

  const { data: fieldsResult, isLoading: fieldsLoading } = useProjectFields(projectId ?? '');
  const customFields = fieldsResult?.items ?? [];

  const showForm = panel.view !== 'list';

  function handleOpenChange(value: boolean) {
    if (!value) setPanel({ view: 'list' });
    setOpen(value);
  }

  function toggleColumn(col: TaskColumnId, checked: boolean) {
    if (col === 'title') return;
    onColumnsChange(checked ? [...visibleColumns, col] : visibleColumns.filter((c) => c !== col));
  }

  const selectedType: FieldType | undefined =
    panel.view === 'create'
      ? panel.fieldType
      : panel.view === 'edit'
        ? (resolveFieldType(panel.field.fieldType) ?? undefined)
        : undefined;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-6 shrink-0"
          aria-label={t('listView.addColumn')}
          data-testid="tasks-listview-column-selector-panel"
        >
          <Plus className="size-3.5" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className={cn(
          'p-0 overflow-hidden transition-[width] duration-200',
          showForm ? 'w-130' : 'w-60',
        )}
      >
        <div className="flex h-full">
          {/* Left panel — always visible */}
          <div className={cn('flex flex-col shrink-0', showForm ? 'w-60 border-r' : 'w-full')}>
            <Tabs defaultValue="existing">
              <TabsList className="w-full rounded-none border-b h-10 bg-transparent px-0">
                <TabsTrigger
                  value="existing"
                  className="flex-1 rounded-none h-full data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none data-[state=active]:bg-transparent"
                  data-testid="tasks-listview-tab-existing"
                >
                  {t('listView.tabExisting')}
                </TabsTrigger>
                {projectId && (
                  <TabsTrigger
                    value="create"
                    className="flex-1 rounded-none h-full data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none data-[state=active]:bg-transparent"
                    data-testid="tasks-listview-tab-create"
                  >
                    {t('listView.tabCreate')}
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Tab: Agregar existente */}
              <TabsContent value="existing" className="mt-0">
                <div className="flex flex-col gap-1 p-2">
                  {/* Built-in columns — always present */}
                  {BUILT_IN_COLUMN_DEFS.map((col) => {
                    const Icon = col.icon;
                    return (
                      <div
                        key={col.id}
                        className="flex items-center gap-2 rounded px-1 py-1 hover:bg-accent"
                      >
                        <Checkbox
                          id={`col-${col.id}`}
                          checked={visibleColumns.includes(col.id)}
                          onCheckedChange={(v) => toggleColumn(col.id, !!v)}
                        />
                        <Icon
                          className={cn('h-3.5 w-3.5 shrink-0', col.iconColorClass)}
                          aria-hidden
                        />
                        <Label htmlFor={`col-${col.id}`} className="cursor-pointer text-xs">
                          {col.labelKey ? t(col.labelKey) : (col.label ?? col.id)}
                        </Label>
                      </div>
                    );
                  })}

                  {/* Custom field columns — only when projectId is provided */}
                  {projectId && (
                    <>
                      <Separator className="my-1" />
                      <p className="px-1 py-0.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        {t('listView.customFieldsSection')}
                      </p>
                      {fieldsLoading && (
                        <div
                          className="flex flex-col gap-1 px-1"
                          data-testid="tasks-listview-custom-fields-loading"
                        >
                          {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} className="h-6 w-full rounded" />
                          ))}
                        </div>
                      )}
                      {!fieldsLoading && customFields.length === 0 && (
                        <p
                          className="px-1 py-1 text-xs text-muted-foreground"
                          data-testid="tasks-listview-custom-fields-empty"
                        >
                          {t('listView.customFieldsEmpty')}
                        </p>
                      )}
                      {customFields.map((field) => {
                        const ft = resolveFieldType(field.fieldType);
                        const Icon = ft != null ? FIELD_REGISTRY[ft]?.icon : undefined;
                        return (
                          <div
                            key={field.id}
                            className="flex items-center gap-2 rounded px-1 py-1 hover:bg-accent"
                          >
                            <Checkbox
                              id={`col-cf-${field.id}`}
                              checked={visibleColumns.includes(field.id ?? '')}
                              onCheckedChange={(v) => toggleColumn(field.id ?? '', !!v)}
                            />
                            {Icon && (
                              <Icon
                                className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                                aria-hidden
                              />
                            )}
                            <Label
                              htmlFor={`col-cf-${field.id}`}
                              className="cursor-pointer text-xs"
                            >
                              {field.name}
                            </Label>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </TabsContent>

              {/* Tab: Crear nuevo */}
              {projectId && (
                <TabsContent value="create" className="mt-0 overflow-y-auto max-h-96">
                  <ExistingFieldsList
                    projectId={projectId}
                    onEdit={(field) => setPanel({ view: 'edit', field })}
                  />
                  <div className="border-t pt-1">
                    <p className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {t('listView.createNew')}
                    </p>
                    <FieldTypeList
                      compact
                      onSelectType={(type) => setPanel({ view: 'create', fieldType: type })}
                      selectedType={selectedType}
                    />
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </div>

          {/* Right panel — field form */}
          {showForm && (
            <div className="flex-1 overflow-y-auto max-h-125">
              <FieldCreateForm
                fieldType={
                  panel.view === 'create' ? panel.fieldType : (selectedType ?? FIELD_TYPE.Text)
                }
                projectId={projectId!}
                editingField={panel.view === 'edit' ? panel.field : undefined}
                onSuccess={() => setPanel({ view: 'list' })}
                onCancel={() => setPanel({ view: 'list' })}
              />
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
